/**
 * The Inventory Audit page's Download button.
 *
 * One button, one workbook — a sheet per flag, in the worklist's own order
 * (Ian, 2026-08-13). It replaces the old two-option dropdown, whose "Current
 * View" export only made sense while the sheet view existed to have a current
 * view; that branch is parked behind SHEET_VIEW_ENABLED, and its DownloadMenu
 * is parked with it.
 *
 * Deliberately independent of AuditContext. The old menu read the sheet
 * view's filters, sort and search to build a filtered export, and none of that
 * describes what an operator wants from Download now: the whole run, laid out
 * the way they read it.
 */
import React, { useState } from "react"
import { Download } from "lucide-react"

import { AuditRooftopOption, AuditRunSummary, exportWorklistWorkbook } from "../../api/audit"
import { THEME } from "../../config/theme"
import { parseUtcTimestamp } from "../../utils/parseUtcTimestamp"

interface Props {
  currentRun: AuditRunSummary | null
  /** Page-level store view — scopes the workbook to one store and stamps the
   *  store into the filename so a scoped file can't pass for the full run.
   *  Null downloads the whole run, exactly as before. */
  rooftop?: AuditRooftopOption | null
}

// parseUtcTimestamp, not bare `new Date`: the API serializes naive-UTC
// timestamps with no offset, which `new Date` reads as LOCAL time. The filename
// then named a different day than RunSelector and the freshness bar showed for
// the same run — and disagreed between SQLite locally and Postgres in
// production, which serialize the offset differently.
function filenameFor(
  run: AuditRunSummary | null,
  rooftop: AuditRooftopOption | null,
): string {
  const when = run?.started_at ? parseUtcTimestamp(run.started_at) : new Date()
  const pad = (n: number) => String(n).padStart(2, "0")
  // "Mazda / Kia" would read as a directory in a filename — flatten it.
  const storeSuffix = rooftop ? ` - ${rooftop.name.replace(/\//g, "-")}` : ""
  return `INVI Audit ${pad(when.getMonth() + 1)}-${pad(when.getDate())}-${when.getFullYear()}${storeSuffix}.xlsx`
}

export default function WorklistDownloadButton({ currentRun, rooftop = null }: Props) {
  const [downloading, setDownloading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const disabled = downloading || currentRun === null

  const handleDownload = async () => {
    if (currentRun === null) return
    setDownloading(true)
    setError(null)
    try {
      const blob = await exportWorklistWorkbook(currentRun.id, rooftop?.slug ?? null)
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.download = filenameFor(currentRun, rooftop)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (err) {
      // Surfaced next to the button rather than thrown: a failed download must
      // not take the audit page down with it.
      setError(err instanceof Error ? err.message : "Download failed")
    } finally {
      setDownloading(false)
    }
  }

  return (
    <div style={{ display: "flex", alignItems: "center", gap: THEME.spacing.sm }}>
      <button
        type="button"
        onClick={handleDownload}
        disabled={disabled}
        title="Download this run as an Excel workbook — one sheet per flag"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          padding: "7px 14px",
          fontSize: 13,
          fontWeight: 500,
          fontFamily: THEME.fonts.primary,
          borderRadius: THEME.borderRadius.sm,
          border: `1px solid ${THEME.colors.silver}`,
          background: THEME.colors.white,
          color: THEME.colors.neutral.light.textPrimary,
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <Download size={15} />
        {downloading ? "Preparing…" : "Download"}
      </button>
      {error !== null && (
        <span style={{ fontSize: 12, color: THEME.colors.semantic.danger }}>{error}</span>
      )}
    </div>
  )
}
